import React, { useMemo } from 'react';
import { Pencil, Plus, AlertCircle, TrendingUp, Briefcase } from 'lucide-react';
import {
  PayrollPosition,
  PayrollDepartment,
  PayrollSettings,
  PAYROLL_DEPARTMENTS,
  PAYROLL_DEPARTMENT_LABELS,
  PAYROLL_STATUS_LABELS,
  DEFAULT_PAYROLL_SETTINGS,
} from '../../types/finances';
import { positionMonthlyCost } from '../../utils/financesForecast';
import { nextScheduledRaise } from '../../utils/payrollCompSchedule';
import { CRMUser } from '../../types';
import PayrollCostBreakdown from './PayrollCostBreakdown';

interface PayrollTableProps {
  positions: PayrollPosition[];
  crmUsers: CRMUser[];
  settings?: PayrollSettings;
  /** True when any position still carries the legacy `benefitsAndTaxRate` field. */
  hasLegacyPositions?: boolean;
  onEdit: (position: PayrollPosition) => void;
  onAdd: () => void;
  onCreateJobPosting?: (position: PayrollPosition) => void;
  readOnly?: boolean;
}

const formatCurrency = (value: number) =>
  new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 }).format(value);

const formatDate = (d: Date | null | undefined): string => {
  if (!d) return '—';
  const dt = new Date(d);
  if (Number.isNaN(dt.getTime())) return '—';
  return dt.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
};

const ROW_GRID = '2fr 1fr 1.2fr 1.2fr 1fr 1fr 80px';

const formatRaiseBadgeDate = (d: Date): string =>
  d.toLocaleDateString('en-US', { month: 'short', year: 'numeric' });

const PayrollTable: React.FC<PayrollTableProps> = ({
  positions,
  crmUsers,
  settings = DEFAULT_PAYROLL_SETTINGS,
  hasLegacyPositions = false,
  onEdit,
  onAdd,
  onCreateJobPosting,
  readOnly = false,
}) => {
  const userById = useMemo(() => {
    const m = new Map<string, CRMUser>();
    crmUsers.forEach(u => m.set(u.uid, u));
    return m;
  }, [crmUsers]);

  const grouped = useMemo(() => {
    const m: Record<PayrollDepartment, PayrollPosition[]> = {
      shop: [], office: [], sales: [], engineering: [], leadership: [], other: [],
    };
    for (const p of positions) m[p.department].push(p);
    for (const d of PAYROLL_DEPARTMENTS) {
      m[d].sort((a, b) => {
        const order = { filled: 0, open: 1, planned: 2 } as const;
        if (order[a.status] !== order[b.status]) return order[a.status] - order[b.status];
        return a.title.localeCompare(b.title);
      });
    }
    return m;
  }, [positions]);

  const nowMonth = useMemo(() => new Date(), []);

  if (positions.length === 0) {
    return (
      <div className="fin-table-wrap">
        <div className="fin-table-empty">
          <p style={{ margin: 0, fontWeight: 600, color: 'var(--pm-text-secondary)' }}>No positions yet</p>
          <span className="fin-table-empty-sub">Add current employees plus planned future hires to model loaded payroll cost across the forecast.</span>
          {!readOnly && (
            <div style={{ marginTop: 14 }}>
              <button type="button" className="fin-primary-btn" onClick={onAdd}>
                <Plus size={14} /> New position
              </button>
            </div>
          )}
        </div>
      </div>
    );
  }

  return (
    <>
      {hasLegacyPositions && (
        <div className="fin-legacy-banner" role="status">
          <AlertCircle size={16} />
          <span>
            <strong>Action needed:</strong> one or more positions still use the
            legacy single-burden % model. Re-open each highlighted position and
            confirm the new health / PTO / SEP / employer-tax / extras fields.
          </span>
        </div>
      )}
      <div className="fin-table-wrap">
        <div className="fin-table-header" style={{ gridTemplateColumns: ROW_GRID }}>
          <span className="fin-th">Title</span>
          <span className="fin-th">Status</span>
          <span className="fin-th right">Salary</span>
          <span className="fin-th right">Loaded / mo</span>
          <span className="fin-th">Start</span>
          <span className="fin-th">Linked user</span>
          <span className="fin-th right">Actions</span>
        </div>

        {PAYROLL_DEPARTMENTS.map(dept => {
          const items = grouped[dept];
          if (items.length === 0) return null;
          const groupMonthly = items.reduce(
            (sum, p) => {
              const linked = p.crmUserId ? userById.get(p.crmUserId) : null;
              return sum + positionMonthlyCost(p, nowMonth, settings, {
                crmUserLocation: linked?.location,
              }).total;
            },
            0,
          );
          return (
            <div key={dept} className="fin-table-group">
              <div className="fin-table-group-header">
                <span className="fin-table-group-color" />
                <span className="fin-table-group-name">{PAYROLL_DEPARTMENT_LABELS[dept]}</span>
                <span className="fin-table-group-count">{items.length}</span>
                <span className="fin-table-group-total">{formatCurrency(groupMonthly)}/mo</span>
              </div>
              {items.map(p => {
                const linked = p.crmUserId ? userById.get(p.crmUserId) : null;
                const cost = positionMonthlyCost(p, nowMonth, settings, {
                  crmUserLocation: linked?.location,
                });
                const isLegacy = p.benefitsAndTaxRate != null;
                const upcomingRaise = nextScheduledRaise(p);
                return (
                  <div
                    key={p.id}
                    className={`fin-table-row${p.isActive ? '' : ' inactive'}`}
                    style={{ gridTemplateColumns: ROW_GRID }}
                    onClick={readOnly ? undefined : () => onEdit(p)}
                    role={readOnly ? undefined : 'button'}
                    tabIndex={readOnly ? undefined : 0}
                    onKeyDown={readOnly ? undefined : evt => {
                      if (evt.key === 'Enter') onEdit(p);
                    }}
                  >
                    <div className="fin-cell">
                      <div className="fin-cell-primary">
                        {p.title}
                        {isLegacy && (
                          <span
                            className="fin-peremp-badge"
                            style={{ background: 'var(--color-warning-light)', color: 'var(--color-warning-text)', borderColor: 'var(--color-warning)' }}
                            title="Legacy burden % — open to confirm itemized fields"
                          >
                            Needs review
                          </span>
                        )}
                        {upcomingRaise && (
                          <span
                            className="fin-peremp-badge fin-raise-badge"
                            title={
                              upcomingRaise.label ||
                              `${upcomingRaise.kind === 'percent' ? `${upcomingRaise.amount}%` : `$${upcomingRaise.amount}`} raise`
                            }
                          >
                            <TrendingUp size={11} aria-hidden />
                            Raise {formatRaiseBadgeDate(new Date(upcomingRaise.effectiveDate))}
                          </span>
                        )}
                      </div>
                      {p.notes && <div className="fin-cell-sub">{p.notes.split('\n')[0]}</div>}
                    </div>
                    <div className="fin-cell">
                      <span className="fin-pill fin-pill-status" data-status={p.status}>
                        {PAYROLL_STATUS_LABELS[p.status]}
                      </span>
                    </div>
                    <div className="fin-cell fin-cell-money">
                      {formatCurrency(p.annualSalary)}
                      {p.payType === 'hourly' && p.hourlyRate != null && (
                        <div className="fin-cell-sub" style={{ textAlign: 'right' }}>
                          ${p.hourlyRate.toFixed(2)}/hr
                          {p.hoursPerWeek ? ` × ${p.hoursPerWeek}h/wk` : ''}
                        </div>
                      )}
                    </div>
                    <div className="fin-cell fin-cell-money">
                      <span
                        className="fin-loaded-tooltip-wrapper"
                        tabIndex={0}
                        aria-label={`Loaded monthly cost ${formatCurrency(cost.total)} — view breakdown`}
                      >
                        {formatCurrency(cost.total)}
                        <span className="fin-loaded-tooltip" role="tooltip">
                          <div className="fin-cost-breakdown-title">Loaded breakdown</div>
                          <PayrollCostBreakdown
                            cost={cost}
                            ptoDays={p.ptoDaysPerYear}
                            extrasNote={p.monthlyEmployeeExtrasNote}
                          />
                        </span>
                      </span>
                    </div>
                    <div className="fin-cell fin-cell-muted">{formatDate(p.startDate)}</div>
                    <div className="fin-cell fin-cell-muted">
                      {linked ? linked.displayName : <span style={{ color: 'var(--pm-text-muted)' }}>—</span>}
                    </div>
                    <div className="fin-row-actions">
                      {!readOnly && (p.status === 'open' || p.status === 'planned') && onCreateJobPosting && (
                        <button
                          type="button"
                          className="fin-icon-btn"
                          onClick={evt => { evt.stopPropagation(); onCreateJobPosting(p); }}
                          aria-label={`Create job posting for ${p.title}`}
                          title="Create job posting"
                        >
                          <Briefcase size={14} />
                        </button>
                      )}
                      {!readOnly && (
                        <button
                          type="button"
                          className="fin-icon-btn"
                          onClick={evt => { evt.stopPropagation(); onEdit(p); }}
                          aria-label={`Edit ${p.title}`}
                        >
                          <Pencil size={14} />
                        </button>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          );
        })}
      </div>
    </>
  );
};

export default PayrollTable;
